import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Badge } from "../../@/components/ui/badge";
import { Button } from "../../@/components/ui/button";
import { Eye } from "lucide-react";
import MarkdownRenderer from "./markdownRenderer/MarkdownRenderer";
import UpdateReviewDialog from "../forms/editProjectReview";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { getUserTimezone, formatUtcToUserTimezone } from "../lib/timezone";

const ReviewedCard = ({
  submission = {},
  courseName,
  courseSlug,
  topicTitle,
  userFirstName,
  userLastName,
  userEmail,
  gradedByFirstName,
  gradedByLastName,
  initialTmReview = null,
  onReviewUpdated
}) => {
  const { getToken } = useAuth();
  const effectiveProject = submission?.project && typeof submission.project === "object" ? submission.project : null;

  const attemptCount = Number(
    submission?.attempt_count ??
      submission?.attemptCount ??
      submission?.project?.attempt_count ??
      0
  );
  const attemptLabel = attemptCount > 0 ? `ATTEMPT ${attemptCount}` : "ATTEMPT —";

  const handleUpdateReview = () => {
    console.log("Updating review for project:", submission.project_id);
  };

  const [tmReview, setTmReview] = useState(null);
  const [tmReviewLoading, setTmReviewLoading] = useState(false);

  const normalizeTmReview = (value) => {
    if (!value || typeof value !== "object") return null;
    const nested = value.review && typeof value.review === "object" ? value.review : null;

    const scoreRaw = value.score ?? nested?.score;
    const scoreParsed =
      typeof scoreRaw === "string" ? Number(scoreRaw) : scoreRaw;
    const normalizedScore =
      typeof scoreParsed === "number" && Number.isFinite(scoreParsed)
        ? scoreParsed
        : scoreParsed ?? null;

    return {
      ...value,
      id: value.id ?? nested?.id ?? null,
      reviewed_at: value.reviewed_at ?? nested?.reviewed_at ?? null,
      reviewed_by: value.reviewed_by ?? nested?.reviewed_by ?? null,
      project_id: value.project_id ?? nested?.project_id ?? null,
      student_id: value.student_id ?? nested?.student_id ?? null,
      score: normalizedScore,
      feedback:
        value.feedback ??
        nested?.feedback ??
        "",
      is_passed: value.is_passed ?? nested?.is_passed,
      reviewer: value.reviewer ?? nested?.reviewer,
      review: nested ?? value.review,
    };
  };

  const pickLatestTmReview = (reviews) => {
    if (!Array.isArray(reviews) || reviews.length === 0) return null;
    const sorted = [...reviews].sort((a, b) => {
      const aTime = a?.reviewed_at ? Date.parse(a.reviewed_at) : NaN;
      const bTime = b?.reviewed_at ? Date.parse(b.reviewed_at) : NaN;
      if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime !== bTime) {
        return aTime - bTime;
      }
      const aId = typeof a?.id === "number" ? a.id : -1;
      const bId = typeof b?.id === "number" ? b.id : -1;
      return aId - bId;
    });
    return sorted[sorted.length - 1] ?? null;
  };

  const userTimezone = React.useMemo(() => getUserTimezone(), []);

  const formatDateForDisplay = (value) => formatUtcToUserTimezone(value, userTimezone);


  const fetchTmReviews = useCallback(async () => {
    const projectId =
      submission?.project_id ??
      submission?.projectId ??
      submission?.project?.id ??
      submission?.project?.project_id ??
      submission?.project?.projectId;
    const studentId = submission?.user_id;
    if (!projectId || !studentId) return;

    setTmReviewLoading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const res = await axios.post(
        api_endpoint + "/project/get/reviews",
        {
          project_id: projectId,
          student_id: studentId,
        },
        reqConfig
      );

      const tm = res?.data?.reviews?.tm_reviews;
      console.log(`TM Reviews: ${projectId}`, tm);
      setTmReview(normalizeTmReview(pickLatestTmReview(tm)));
    } catch {
      setTmReview(null);
    } finally {
      setTmReviewLoading(false);
    }
  }, [submission?.project_id, submission?.user_id, getToken]);

  useEffect(() => {
    setTmReview(normalizeTmReview(initialTmReview));
  }, [initialTmReview]);

  const viewProjectLink = useMemo(() => {
    const normalizeExternalUrl = (value) => {
      if (!value || typeof value !== "string") return null;
      const trimmed = value.trim();
      if (!trimmed) return null;

      const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

      try {
        const parsed = new URL(withProtocol);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
        return parsed.toString();
      } catch {
        return null;
      }
    };

    const liveUrl = normalizeExternalUrl(
      submission.live_url ||
        submission.project?.live_url
    );
    if (liveUrl) {
      return { url: liveUrl, title: "Open live project" };
    }

    const repoUrl = normalizeExternalUrl(
      submission.submission_url ||
        submission.github_repo ||
        submission.project?.submission_url ||
        submission.project?.github_repo
    );
    if (repoUrl) {
      return { url: repoUrl, title: "Live URL missing — opening repository instead" };
    }

    const fileUrl = normalizeExternalUrl(
      submission.file_url ||
        submission.project?.file_url
    );
    if (fileUrl) {
      return { url: fileUrl, title: "Live URL missing — opening submitted file instead" };
    }

    return { url: null, title: "No URL available for this project" };
  }, [submission]);

  const getStatusBadge = (is_passed) => {
    const styles = {
      PASSED: "bg-[var(--color-success)] text-white",
      FAILED: "bg-[var(--color-destructive)] text-white",
    };
    const status = is_passed ? "PASSED" : "FAILED";
    return (
      <Badge
        className={`${styles[status] || styles.PASSED
          } font-medium font-heading`}
      >
        {status}
      </Badge>
    );
  };

  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

  const passmarkPoints = useMemo(() => {
    const maxPointsRaw = effectiveProject?.score ?? submission?.project?.score ?? 100;
    const maxPoints = Number(maxPointsRaw);
    const safeMaxPoints = Number.isFinite(maxPoints) && maxPoints > 0 ? maxPoints : 100;

    const passmarkPercentRaw = effectiveProject?.passmark ?? submission?.project?.passmark ?? 50;
    const passmarkPercent = Number(passmarkPercentRaw);
    const safePassmarkPercent = Number.isFinite(passmarkPercent) ? passmarkPercent : 50;

    return Math.round((safePassmarkPercent / 100) * safeMaxPoints);
  }, [effectiveProject?.passmark, effectiveProject?.score, submission?.project?.passmark, submission?.project?.score]);

  const computedIsPassed = useMemo(() => {
    if (tmReview?.score !== undefined && tmReview?.score !== null) {
      return Number(tmReview.score) >= (passmarkPoints ?? 0);
    }
    return Boolean(submission?.is_passed);
  }, [tmReview?.score, passmarkPoints, submission?.is_passed]);

  return (
    <>
      <div className="bg-(--surface-base) border-2 border-(--neutral-300) rounded-lg overflow-hidden m-0 mb-4">
        {/* Header */}
        <div className="py-2 px-4 border-b border-(--neutral-300)">
          <div className="flex flex-row justify-between items-start flex-wrap gap-3">
            {/* Left section: courseSlug, title, badge, name, time */}
            <div className="flex flex-col gap-2 min-w-0 flex-1">
              {/* Title row */}
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-(--neutral-900) font-heading wrap-break-word">
                  <span className="uppercase">{courseSlug ? `${courseSlug} - ` : ""}</span>
                  {/* {topicTitle ? `${topicTitle} - ` : ""} */}
                  {effectiveProject?.title ? (
                    effectiveProject.title
                  ) : (
                    <span className="text-(--neutral-400)">No title available</span>
                  )}
                </h3>
              </div>
              {/* Badge + user details row */}
              <div className="flex flex-wrap items-center gap-2 text-sm text-(--neutral-600) mb-1">
                <Badge
                  variant="outline"
                  className="border-2 font-heading uppercase"
                >
                  {attemptLabel}
                </Badge>

                <div className="text-sm text-(--neutral-600) mb-1">
                  <span className="font-body">Submitted by </span>
                  <span className="font-medium font-body">
                    {userFirstName || userLastName
                      ? `${userFirstName ?? ""} ${userLastName ?? ""}`.trim()
                      : "Name Not available"}
                  </span>
                  <span className="font-body">
                    {" "}
                    •{" "}
                    {formatDateForDisplay(submission.submitted_on)}
                  </span>
                </div>
              </div>
            </div>
            {/* Right section: score and status badge */}
            <div className="flex flex-col items-end min-w-20">
              <div className="text-3xl font-extrabold text-(--color-primary) font-body">
                {tmReview?.score !== undefined && tmReview?.score !== null ? (
                  tmReview.score
                ) : submission.best_score !== undefined ? (
                  submission.best_score
                ) : (
                  <span className="text-(--neutral-400)">N/A</span>
                )}
                <span className="text-lg text-(--color-primary) font-body">
                  /{effectiveProject?.score ?? submission.project?.score ?? "—"}
                </span>
              </div>
              {getStatusBadge(computedIsPassed)}
            </div>
          </div>
        </div>

        {/* Assessment content: remove bottom padding so footer sits flush */}
        <div className="px-4 sm:px-6 pt-6 pb-0">
          <div className="mb-6">
            {tmReviewLoading ? (
              <span className="text-(--neutral-400)">Loading feedback…</span>
            ) : tmReview?.feedback && String(tmReview.feedback).trim() ? (
              <div className="prose prose-lg dark:prose-invert max-w-none font-body">
                <MarkdownRenderer content={tmReview.feedback} />
              </div>
            ) : (
              <span className="text-(--neutral-400)">Not available yet</span>
            )}
          </div>

          {/* {submission.comment ? (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-(--neutral-900) font-heading mb-2">
                Student Comment
              </h4>
              <div className="prose prose-lg dark:prose-invert max-w-none font-body">
                <MarkdownRenderer content={submission.comment} />
              </div>
            </div>
          ) : null} */}

          {/* Footer */}
          <div className="border-t border-(--neutral-300) bg-(--neutral-100) -mx-6 px-6 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
              <div className="text-base text-(--neutral-600) font-body mb-2">
                <span className="font-body">Feedback by </span>
                <span className="font-medium font-body">
                  {gradedByFirstName || gradedByLastName
                    ? `${gradedByFirstName ?? ""} ${gradedByLastName ?? ""}`.trim()
                    : "Name Not available"}
                </span>
                <span className="font-body">
                  {" "}
                  •{" "}
                  {formatDateForDisplay(tmReview?.reviewed_at || submission.last_updated_at)}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                {viewProjectLink.url ? (
                  <Button
                    asChild
                    variant="link"
                    size="base"
                    className="text-(--color-primary) hover:text-accent font-body w-full sm:w-auto"
                    title={viewProjectLink.title}
                  >
                    <a
                      href={viewProjectLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Project
                    </a>
                  </Button>
                ) : (
                  <Button
                    variant="link"
                    size="base"
                    disabled
                    className="text-(--color-primary) font-body w-full sm:w-auto"
                    title={viewProjectLink.title}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Project
                  </Button>
                )}
                <Button
                  onClick={() => setUpdateDialogOpen(true)}
                  variant="link"
                  size="base"
                  className="text-(--color-primary) hover:text-accent font-body w-full sm:w-auto"
                >
                  {/* <Edit className="w-4 h-4 mr-1" /> */}
                  Update Review
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <UpdateReviewDialog
        open={updateDialogOpen}
        onOpenChange={setUpdateDialogOpen}
        submission={submission}
        existingReview={tmReview}
        gradedBy={gradedByFirstName + " " + gradedByLastName}
        courseName={courseName}
        userFirstName={userFirstName}
        userLastName={userLastName}
        userEmail={userEmail}
        onReviewUpdated={onReviewUpdated}
        onSaved={(savedPayload) => {
          const candidate =
            savedPayload?.review ??
            savedPayload?.tm_review ??
            savedPayload?.data?.review ??
            savedPayload;

          const normalizedCandidate = normalizeTmReview(candidate);
          if (normalizedCandidate?.score !== null || (normalizedCandidate?.feedback && String(normalizedCandidate.feedback).trim())) {
            setTmReview(normalizedCandidate);
          }

          fetchTmReviews();
          setTimeout(() => {
            fetchTmReviews();
          }, 650);
        }}
      />
    </>
  );
};

export default ReviewedCard;
